import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { SignupShell } from './SignupShell';
import { errorKeyFor, fetchSignupProgress, type SignupProgress } from './signupApi';
import { clearSignupSession, loadSignupSession, type SignupCheckoutSession } from './signupSession';
import { useCheckoutLauncher } from './useCheckoutLauncher';

/** Contract §3.4: every 3 s, for about five minutes. */
export const POLL_INTERVAL_MS = 3000;
export const MAX_POLLS = 100;

/**
 * `success` / `cancel` are the fixed return URLs Stripe sends the browser to;
 * `status` is our own neutral entry (reload, "check status"). The route names
 * only choose the wording — what is true comes from the status endpoint.
 */
export type ProgressSource = 'success' | 'cancel' | 'status';

type Phase = 'loading' | 'live' | 'timedOut' | 'sessionLost' | 'failed';

/** Whether the next poll can still change the answer. */
function shouldPoll(progress: SignupProgress, source: ProgressSource): boolean {
  if (progress.stage === 'provisioning') return true;
  // Just back from Stripe the webhook may not have landed yet: an open session
  // with no issue is "not confirmed yet", not "not paid".
  return (
    source === 'success' &&
    (progress.stage === 'awaiting_payment' || progress.stage === 'awaiting_checkout') &&
    progress.issue === null
  );
}

/**
 * Where the browser lands after Stripe (#3017), and where the signup is
 * followed until the workspace exists. The URL never decides anything: no
 * query parameter is read, and "paid", "provisioning" and "ready" are shown
 * only when `POST /signup/checkout/status` says so.
 */
export default function SignupProgressPage({ source }: { source: ProgressSource }) {
  const { t } = useTranslation('signup');
  const [session] = useState<SignupCheckoutSession | null>(() => loadSignupSession());
  const [phase, setPhase] = useState<Phase>(session ? 'loading' : 'sessionLost');
  const [progress, setProgress] = useState<SignupProgress | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [transient, setTransient] = useState(false);
  const [run, setRun] = useState(0);

  const recheck = useCallback(() => {
    setPhase('loading');
    setRun((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;

    const schedule = (): void => {
      if (polls >= MAX_POLLS) {
        setPhase('timedOut');
        return;
      }
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    const poll = async (): Promise<void> => {
      polls += 1;
      const result = await fetchSignupProgress(session);
      if (cancelled) return;

      if (!result.ok) {
        if (result.code === 'invalid_capability') {
          clearSignupSession();
          setPhase('sessionLost');
          return;
        }
        if (result.status === 400) {
          setErrorCode(result.code);
          setPhase('failed');
          return;
        }
        // Network, 429, 5xx: the payment does not depend on this page; keep trying.
        setErrorCode(result.code);
        setTransient(true);
        schedule();
        return;
      }

      setTransient(false);
      setErrorCode(null);
      setProgress(result.data);
      setPhase('live');
      if (shouldPoll(result.data, source)) schedule();
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [session, source, run]);

  const launcher = useCheckoutLauncher(session, (failure) => {
    if (failure.code === 'invalid_capability') {
      clearSignupSession();
      setPhase('sessionLost');
    } else if (failure.code === 'signup_not_payable') {
      recheck();
    }
  });

  const startAgain = (
    <Button component={RouterLink} to="/signup" variant="contained" sx={{ mt: 3 }}>
      {t('startAgain')}
    </Button>
  );
  const checkAgain = (
    <Button variant="outlined" onClick={recheck} sx={{ mt: 3, mr: 1 }}>
      {t('checkStatus')}
    </Button>
  );

  const payButton = (
    <>
      {launcher.errorCode ? (
        <Alert severity="error" role="alert" sx={{ mt: 2 }}>
          {t(errorKeyFor(launcher.errorCode))}
        </Alert>
      ) : null}
      <Button
        variant="contained"
        sx={{ mt: 3 }}
        disabled={launcher.pending || launcher.errorCode === 'checkout_not_configured'}
        onClick={() => void launcher.launch()}
        startIcon={launcher.pending ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {launcher.pending
          ? t('redirecting')
          : launcher.errorCode
            ? t('retryPayment')
            : t('continuePayment')}
      </Button>
    </>
  );

  const waiting = (message: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} role="status">
      <CircularProgress size={24} />
      <Typography>{message}</Typography>
    </Box>
  );

  const renderProgress = (current: SignupProgress, timedOut: boolean) => {
    const issueText = current.issue ? t(`issues.${current.issue}`) : null;
    switch (current.stage) {
      case 'provisioning':
        return timedOut ? (
          <>
            <Alert severity="warning" role="alert">
              {t('stage.provisioningTimeout')}
            </Alert>
            {checkAgain}
          </>
        ) : (
          waiting(t('stage.provisioning'))
        );
      case 'provisioning_failed':
        return (
          <Alert severity="warning" role="alert">
            {issueText ?? t('stage.provisioning_failed')}
          </Alert>
        );
      case 'failed':
        return (
          <Alert severity="warning" role="alert">
            {issueText ?? t('stage.failed')}
          </Alert>
        );
      case 'ready':
        return (
          <>
            <Alert severity="success" role="status">
              <Typography fontWeight={600}>{t('stage.ready')}</Typography>
              {current.slug ? (
                <Typography variant="body2">{t('workspaceLabel', { slug: current.slug })}</Typography>
              ) : null}
              <Typography variant="body2">{t('readyHint')}</Typography>
            </Alert>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              sx={{ mt: 3 }}
              onClick={clearSignupSession}
            >
              {t('goToLogin')}
            </Button>
          </>
        );
      case 'expired':
        return (
          <>
            <Alert severity="warning" role="alert">
              {t('stage.expired')}
            </Alert>
            {startAgain}
          </>
        );
      case 'awaiting_checkout':
      case 'awaiting_payment': {
        if (issueText === null && source === 'success') {
          return timedOut ? (
            <>
              <Alert severity="warning" role="alert">
                {t('stage.confirmingTimeout')}
              </Alert>
              {checkAgain}
              {payButton}
            </>
          ) : (
            waiting(t('stage.confirming'))
          );
        }
        const lead = issueText ?? (source === 'cancel' ? t('stage.cancelled') : null);
        return (
          <>
            {lead ? (
              <Alert severity={current.issue ? 'warning' : 'info'} role="alert" sx={{ mb: 1 }}>
                {lead}
              </Alert>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {t(`stage.${current.stage}`)}
            </Typography>
            {payButton}
          </>
        );
      }
      default:
        return (
          <>
            <Alert severity="warning" role="alert">
              {t('stage.unknown')}
            </Alert>
            {checkAgain}
          </>
        );
    }
  };

  return (
    <SignupShell title={t('progressTitle')} maxWidth={480}>
      {phase === 'loading' ? waiting(t('checking')) : null}

      {phase === 'sessionLost' ? (
        source === 'success' ? (
          <>
            <Alert severity="info" role="status">
              {t('stage.sessionLostReturn')}
            </Alert>
            <Button component={RouterLink} to="/login" variant="contained" sx={{ mt: 3 }}>
              {t('goToLogin')}
            </Button>
          </>
        ) : (
          <>
            <Alert severity="warning" role="alert">
              {t('stage.sessionLostCancel')}
            </Alert>
            {startAgain}
          </>
        )
      ) : null}

      {phase === 'failed' ? (
        <>
          <Alert severity="error" role="alert">
            {t(errorKeyFor(errorCode ?? 'generic'))}
          </Alert>
          {checkAgain}
        </>
      ) : null}

      {phase === 'live' && progress ? renderProgress(progress, false) : null}
      {phase === 'timedOut' && progress ? renderProgress(progress, true) : null}
      {phase === 'timedOut' && !progress ? (
        <>
          <Alert severity="error" role="alert">
            {t(errorKeyFor(errorCode ?? 'generic'))}
          </Alert>
          {checkAgain}
        </>
      ) : null}

      {transient && phase !== 'timedOut' ? (
        <Alert severity="info" role="status" sx={{ mt: 2 }}>
          {t('statusRetrying')}
        </Alert>
      ) : null}
    </SignupShell>
  );
}
